import React, { useMemo } from 'react';
import DecisionEngineBlock from '../../decision-engine/DecisionEngineBlock';
import {
  aksiyonKilidiHesapla,
  aksiyonSkoruHesapla,
  aksiyonlaraKilitUygula,
  guvenBandiHesapla
} from '../../decision-engine/decisionEngineScoring';
import DrawerFooterActions from './DrawerFooterActions';
import { olusturAksiyonDurumKapsami } from '../../decision-engine/actionStateScope';

function Row({ label, value }) {
  return (
    <li className="flex items-center justify-between text-sm">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="font-black text-slate-900 dark:text-white">{value}</span>
    </li>
  );
}

const formatTry = (value) => `${Number(value || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} TL`;

export default function ExpertDetailDrawer({ entity, onClose }) {
  const payload = entity?.payload || {};
  const experts = payload?.experts || {};
  const impact = payload?.impact || {};

  const totals = experts?.totals || {};
  const revenue = experts?.revenue || {};
  const impactSummary = impact?.summary || {};
  const impactItems = Array.isArray(impact?.items) ? impact.items : [];
  const topExperts = Array.isArray(experts?.topExperts) ? experts.topExperts : [];
  const sampleSize = Number(totals.activeRelationships || totals.totalExperts || 0);

  const decisionLayer = useMemo(() => {
    const avgImpact = Number(impactSummary.averageImpactScore || 0);
    const monthlyRevenue = Number(revenue.monthlyRevenueTry || 0);
    const yearlyRevenue = Number(revenue.yearlyRevenueTry || 0);

    const confidence = guvenBandiHesapla(sampleSize, { yuksek: 80, orta: 25 });

    const anomalies = [];
    if (Number(totals.verifiedExperts || 0) > Number(totals.totalExperts || 0)) {
      anomalies.push({
        title: 'Doğrulanmış uzman sayısı toplamı aşıyor',
        meaning: 'Doğrulanmış uzman sayısı toplam uzman sayısından büyük görünüyor.',
        risk: 'Uzman doğrulama metrikleri yanlış okunursa etki analizi bozulur.',
        recommendation: 'Uzman sayım kaynağı ve doğrulama koşulları yeniden kontrol edilmeli.'
      });
    }
    if (monthlyRevenue === 0 && Number(totals.activeRelationships || 0) > 0) {
      anomalies.push({
        title: 'Aktif ilişki var ama aylık gelir 0',
        meaning: 'Uzman-kullanıcı ilişkisi var ancak gelir metrikleri boş geliyor.',
        risk: 'Gelir akışı eşleşmesi eksikse uzman etkisi yanlış önceliklenir.',
        recommendation: 'Gelir kaydı ve ilişki eşleşmesi aynı pencere için doğrulanmalı.'
      });
    }
    if (avgImpact > 5) {
      anomalies.push({
        title: 'Etki skoru beklenen sınırın üzerinde',
        meaning: 'Etki skoru ölçeği tanımlı aralığı aşıyor olabilir.',
        risk: 'Aşırı yüksek skorlar uzman önceliğini yapay olarak bozabilir.',
        recommendation: 'Etki skoru formülü ve ölçek dönüşümü gözden geçirilmeli.'
      });
    }

    const { actionLock, actionLockReason } = aksiyonKilidiHesapla({
      anomaliSayisi: anomalies.length,
      guvenBandi: confidence,
      anomaliMesaji: 'Bu aksiyon şu an uygulanamaz: uzman verisi tutarsız, önce doğrulama yapılmalı.',
      dusukOrneklemMesaji: 'Bu aksiyon şu an uygulanamaz: örneklem düşük, önce gözlem modunda izlenmeli.'
    });

    const actions = [
      {
        actionKey: 'dusuk-etki-kocluk',
        title: 'Düşük etki skorlu uzmanlara hedefli koçluk döngüsü başlat',
        reason: 'Düşük etki grubunda iyileştirme potansiyeli yüksek.',
        doNotApplyWhen: 'Etki skoru anomali veriyorsa önce hesaplama doğrulanmalı.',
        expectedImpact: 'Etki skorunda ve aktif ilişkide artış',
        affectedSegment: 'Düşük etki skorlu uzman grubu',
        score: aksiyonSkoruHesapla(8, confidence, sampleSize, { yuksek: 80, orta: 25 }),
        enabled: !actionLock,
        lockReason: actionLockReason,
        impacts: ['+0.08 Etki skoru', '+%4 Aktif ilişki', '+70 kullanıcı etki alanı'],
        baselineExpected: {
          metric: 'Ortalama etki skoru',
          baseline: Number(avgImpact).toLocaleString('tr-TR'),
          expected: Number((avgImpact + 0.08).toFixed(2)).toLocaleString('tr-TR')
        },
        context: {
          orneklem: sampleSize,
          segment: 'Uzman Etki Grubu',
          yemekTipi: 'Genel',
          pencere: 'Son 30 Gün'
        },
        status: 'uygulanmadı',
        resultDelta: '',
        updatedAt: new Date().toLocaleDateString('tr-TR'),
        whyThisAction: [
          'Etki dağılımında düşük skorlu grup sistem etkisini aşağı çekiyor.',
          'Koçluk döngüsü düşük riskle hızlı performans artışı sağlar.'
        ]
      },
      {
        actionKey: 'eslestirme-gelir-etki-optimizasyon',
        title: 'Uzman eşleştirme kuralını gelir ve etki birlikte optimize edecek şekilde güncelle',
        reason: 'Aktif ilişki artışı gelir artışıyla aynı hızda ilerlemiyor.',
        doNotApplyWhen: 'Aylık gelir kaydı tutarsızsa eşleştirme kuralı canlıya alınmamalı.',
        expectedImpact: 'Gelir verimliliğinde artış',
        affectedSegment: 'Aktif ilişki havuzu',
        score: aksiyonSkoruHesapla(6, confidence, sampleSize, { yuksek: 80, orta: 25 }),
        enabled: !actionLock,
        lockReason: actionLockReason,
        impacts: ['+%5 Aylık gelir', '+%3 Eşleştirme verimi'],
        baselineExpected: {
          metric: 'Aylık gelir',
          baseline: formatTry(monthlyRevenue),
          expected: formatTry(monthlyRevenue * 1.05)
        },
        context: {
          orneklem: sampleSize,
          segment: 'Aktif Uzman-Kullanıcı Eşleşmesi',
          yemekTipi: 'Genel',
          pencere: 'Son 30 Gün'
        },
        status: 'testte',
        resultDelta: '+%1.4 gelir',
        updatedAt: new Date().toLocaleDateString('tr-TR'),
        whyThisAction: [
          'İlişki artışı gelir artışına tam yansımıyor.',
          'Doğru eşleştirme hem gelir hem etkiyi birlikte artırır.'
        ]
      },
      {
        actionKey: 'yuksek-etki-gorunurluk',
        title: 'Yüksek etki uzmanlarını yüksek değerli segmentlerde daha görünür konumlandır',
        reason: 'Yüksek etki uzmanlarının kapsadığı kullanıcı başına değer daha yüksek.',
        doNotApplyWhen: 'Toplam uzman verisi tutarsızsa yayın artışı ertelenmeli.',
        expectedImpact: 'Segment başına etki ve değer artışı',
        affectedSegment: 'Yüksek değerli segmentler',
        score: aksiyonSkoruHesapla(5, confidence, sampleSize, { yuksek: 80, orta: 25 }),
        enabled: !actionLock,
        lockReason: actionLockReason,
        impacts: ['+0.05 Etki skoru', '+%3 Yıllık gelir'],
        baselineExpected: {
          metric: 'Yıllık gelir',
          baseline: formatTry(yearlyRevenue),
          expected: formatTry(yearlyRevenue * 1.03)
        },
        context: {
          orneklem: sampleSize,
          segment: 'Yüksek Etki Uzman Havuzu',
          yemekTipi: 'Genel',
          pencere: 'Son 30 Gün'
        },
        status: 'uygulanmadı',
        resultDelta: '',
        updatedAt: new Date().toLocaleDateString('tr-TR'),
        whyThisAction: [
          'Yüksek etki uzmanları daha hızlı sonuç üretir.',
          'Görünürlük artışı segment performansını çarpan etkisiyle büyütür.'
        ]
      }
    ];

    const actionsWithLock = aksiyonlaraKilitUygula(actions, actionLock, actionLockReason);
    const priority = avgImpact >= 0.7 ? 'HIGH' : avgImpact >= 0.35 ? 'MEDIUM' : 'LOW';
    const keyTakeaway = avgImpact < 0.35
      ? 'Uzman tarafında en hızlı kazanım, düşük etki grubuna odaklı koçlukla alınır.'
      : 'Uzman tarafında en yüksek kaldıraç, yüksek etki uzmanlarının doğru segmentlere yönlendirilmesidir.';

    return {
      narrative: 'Uzman etkisi, gelir ve aktif ilişki yoğunluğu birlikte okunarak büyüme etkisi en yüksek uzman kümeleri seçilir.',
      keyTakeaway,
      actions: actionsWithLock,
      anomalies,
      priority,
      confidence,
      sampleSize,
      expectedOutcome: 'Doğru uzman-segment eşleşmesi ile etki skorunda +0.05 ila +0.15 artış hedeflenir.',
      actionLock,
      actionLockReason
    };
  }, [impactSummary, revenue, totals, sampleSize]);

  const actionStateScope = olusturAksiyonDurumKapsami('expert', entity);

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `uzman-detay-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <DecisionEngineBlock
        narrative={decisionLayer.narrative}
        keyTakeaway={decisionLayer.keyTakeaway}
        actions={decisionLayer.actions}
        anomalies={decisionLayer.anomalies}
        priority={decisionLayer.priority}
        confidence={decisionLayer.confidence}
        sampleSize={decisionLayer.sampleSize}
        expectedOutcome={decisionLayer.expectedOutcome}
        actionLock={decisionLayer.actionLock}
        actionLockReason={decisionLayer.actionLockReason}
        actionStateScope={actionStateScope}
      />

      <section className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900">
        <p className="text-xs text-slate-500 dark:text-slate-400">Uzman Özet Alanı</p>
        <h4 className="mt-1 text-lg font-black text-slate-900 dark:text-white">
          {Number(impactSummary.averageImpactScore || 0).toLocaleString('tr-TR')}
        </h4>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Ortalama etki | Son 7 gün aktif uzman: {Number(totals.d7ActiveExperts || 0).toLocaleString('tr-TR')}
        </p>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Uzman Kırılımı</p>
        <ul className="mt-2 space-y-1.5">
          <Row label="Toplam uzman" value={Number(totals.totalExperts || 0).toLocaleString('tr-TR')} />
          <Row label="Doğrulanmış uzman" value={Number(totals.verifiedExperts || 0).toLocaleString('tr-TR')} />
          <Row label="Aktif ilişki" value={Number(totals.activeRelationships || 0).toLocaleString('tr-TR')} />
          <Row label="Aylık gelir" value={formatTry(revenue.monthlyRevenueTry)} />
          <Row label="Yıllık gelir" value={formatTry(revenue.yearlyRevenueTry)} />
        </ul>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Etki Listesi</p>
        {impactItems.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Etki listesi bu filtrede boş.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {impactItems.slice(0, 10).map((item) => (
              <li key={item.expertId} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate text-slate-700 dark:text-slate-200">{item.expertName}</span>
                <span className="font-black text-slate-900 dark:text-white">
                  {item.expertImpactScore == null ? 'Yok' : Number(item.expertImpactScore).toLocaleString('tr-TR')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">En Yüksek Portföy Uzmanları</p>
        {topExperts.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Bu filtrede uzman performans kaydı bulunamadı.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {topExperts.slice(0, 10).map((item) => (
              <li key={item.expertId} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate text-slate-700 dark:text-slate-200">{item.fullName}</span>
                <span className="font-black text-slate-900 dark:text-white">
                  {Number(item.activeClientCount || 0).toLocaleString('tr-TR')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <DrawerFooterActions onClose={onClose} onExport={handleExport} />
    </div>
  );
}


